import React from 'react';
import PropTypes from 'prop-types';

import { Thumbnail } from '../Thumbnail';
import { useThumbnailGridLayout } from './useThumbnailGridLayout';

const ThumbnailList = ({
  thumbnails,
  onThumbnailClick,
  onThumbnailDoubleClick,
  onClickUntrack,
  activeDisplaySetInstanceUIDs = [],
  viewPreset,
  onThumbnailContextMenu,
}: withAppTypes) => {
  const [isSmallScreen, setIsSmallScreen] = React.useState(
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(max-width: 1200px)').matches
      : false
  );

  const [isMobileScreen, setIsMobileScreen] = React.useState(
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(max-width: 767px)').matches
      : false
  );

  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 1200px)');
    const handler = (e) => setIsSmallScreen(e.matches);

    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);

  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e) => setIsMobileScreen(e.matches);

    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);

  const gridRef = React.useRef<HTMLDivElement>(null);
  const isThumbnailGrid = viewPreset === 'thumbnails';
  const { thumbnailWidth, gridStyle } = useThumbnailGridLayout(
    gridRef,
    isThumbnailGrid,
    isMobileScreen
  );

  const thumbnailGridClass =
    !isThumbnailGrid
      ? 'grid-cols-1'
      : gridStyle
        ? ''
        : isMobileScreen
          ? 'grid-cols-1'
          : 'grid-cols-2';

  return (
    <div className="w-full h-full flex flex-col">
      <div
        ref={gridRef}
        id="ohif-thumbnail-list"
        style={gridStyle}
        className={`ohif-scrollbar bg-grey-main grid auto-rows-auto content-start items-start overflow-x-hidden overflow-y-auto w-full flex-1 ${
          isMobileScreen
            ? `${thumbnailGridClass} pt-[4px] pr-[2px] pl-[2px] gap-[6px] pb-[8px]`
            : `${thumbnailGridClass} pt-[4px] pr-[4px] pl-[4px] gap-[8px] pb-[12px]`
        }`}
      >
        {thumbnails.map(
          ({
            displaySetInstanceUID,
            description,
            dragData,
            seriesNumber,
            numInstances,
            loadingProgress,
            modality,
            componentType,
            seriesDate,
            countIcon,
            isTracked,
            canReject,
            onReject,
            imageSrc,
            messages,
            imageAltText,
            isHydratedForDerivedDisplaySet,
          }) => {
            const isActive = activeDisplaySetInstanceUIDs.includes(displaySetInstanceUID);
            return (
              <Thumbnail
                key={displaySetInstanceUID}
                cellWidth={isThumbnailGrid ? thumbnailWidth ?? undefined : undefined}
                displaySetInstanceUID={displaySetInstanceUID}
                dragData={dragData}
                description={description}
                seriesNumber={seriesNumber}
                numInstances={numInstances || 1}
                countIcon={countIcon}
                imageSrc={imageSrc}
                imageAltText={imageAltText}
                messages={messages}
                isActive={isActive}
                modality={modality}
                viewPreset={componentType === 'thumbnailNoImage' ? 'list' : viewPreset}
                thumbnailType={componentType}
                onClick={() => onThumbnailClick(displaySetInstanceUID)}
                onDoubleClick={() => onThumbnailDoubleClick(displaySetInstanceUID)}
                isTracked={isTracked}
                loadingProgress={loadingProgress}
                onClickUntrack={() => onClickUntrack(displaySetInstanceUID)}
                isHydratedForDerivedDisplaySet={isHydratedForDerivedDisplaySet}
                canReject={canReject}
                onReject={onReject}
                onThumbnailContextMenu={onThumbnailContextMenu}
              />
            );
          }
        )}
      </div>
    </div>
  );
};

ThumbnailList.propTypes = {
  thumbnails: PropTypes.arrayOf(
    PropTypes.shape({
      displaySetInstanceUID: PropTypes.string.isRequired,
      imageSrc: PropTypes.string,
      imageAltText: PropTypes.string,
      seriesDate: PropTypes.string,
      seriesNumber: PropTypes.any,
      numInstances: PropTypes.number,
      description: PropTypes.string,
      componentType: PropTypes.any,
      isTracked: PropTypes.bool,
      /**
       * Data the thumbnail should expose to a receiving drop target. Use a matching
       * `dragData.type` to identify which targets can receive this draggable item.
       * If this is not set, drag-n-drop will be disabled for this thumbnail.
       *
       * Ref: https://react-dnd.github.io/react-dnd/docs/api/use-drag#specification-object-members
       */
      dragData: PropTypes.shape({
        /** Must match the "type" a dropTarget expects */
        type: PropTypes.string.isRequired,
      }),
    })
  ),
  activeDisplaySetInstanceUIDs: PropTypes.arrayOf(PropTypes.string),
  onThumbnailClick: PropTypes.func.isRequired,
  onThumbnailDoubleClick: PropTypes.func.isRequired,
  onClickUntrack: PropTypes.func.isRequired,
  viewPreset: PropTypes.string,
};

export { ThumbnailList };